/* PushOptInSheet — makes sure every logged user gets REAL system notifications.
   • Permission already granted → silently (re)links the push subscription to
     this user on every app open (fixes "notifiche solo dentro l'app").
   • Permission not asked yet → friendly bottom sheet (max once every 3 days). */
import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BellRing, Radar, Siren, Heart, Footprints, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { usePush } from "@/hooks/usePush";
import { useAuth } from "@/context/AuthContext";

const LS_SNOOZE = "orio_push_optin_snooze_v1";
const SNOOZE_MS = 3 * 24 * 3600 * 1000;

const PERKS = [
  { icon: Radar, label: "ORIO Radar: incendi, caldo estremo, allerte meteo" },
  { icon: Siren, label: "SOS e pericoli segnalati vicino a te" },
  { icon: Heart, label: "Nuovi match, saluti e messaggi" },
  { icon: Footprints, label: "Promemoria passeggiata e obiettivi km" },
  { icon: Sparkles, label: "Consigli quotidiani di ORIO AI" },
];

function cachePosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve();
    navigator.geolocation.getCurrentPosition(
      (p) => {
        try {
          localStorage.setItem("orio_radar_last_pos_v1", JSON.stringify({
            lat: p.coords.latitude, lng: p.coords.longitude, ts: Date.now(),
          }));
        } catch { /* noop */ }
        resolve();
      },
      () => resolve(),
      { timeout: 8000, maximumAge: 3600000 },
    );
  });
}

export default function PushOptInSheet() {
  const { user } = useAuth();
  const { supported, subscribe } = usePush();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const synced = useRef(false);

  useEffect(() => {
    if (!user || !supported || synced.current) return;
    synced.current = true;
    if (Notification.permission === "granted") {
      subscribe();
      return;
    }
    if (Notification.permission !== "default") return;
    const snoozed = Number(localStorage.getItem(LS_SNOOZE) || 0);
    if (Date.now() - snoozed < SNOOZE_MS) return;
    const t = setTimeout(() => setOpen(true), 6000);
    return () => clearTimeout(t);
  }, [user, supported, subscribe]);

  const later = () => {
    try { localStorage.setItem(LS_SNOOZE, String(Date.now())); } catch { /* noop */ }
    setOpen(false);
  };

  const enable = async () => {
    setBusy(true);
    const r = await subscribe();
    setBusy(false);
    if (r.ok) {
      setOpen(false);
      toast.success("Notifiche attive · ORIO ti avviserà anche ad app chiusa");
      await cachePosition();
      subscribe();
    } else if (r.reason === "denied") {
      later();
      toast.error("Notifiche bloccate: attivale da Impostazioni › App › ORIO › Notifiche");
    } else {
      later();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[1400] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="push-optin-sheet">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={later} />
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
            className="relative w-full sm:max-w-md rounded-t-[28px] sm:rounded-[28px] bg-white p-6 pb-[calc(24px+env(safe-area-inset-bottom))]">
            <button onClick={later} aria-label="Chiudi" data-testid="push-optin-close"
              className="absolute top-4 right-4 h-8 w-8 rounded-full bg-[#F4EFE6] flex items-center justify-center">
              <X className="h-4 w-4" />
            </button>
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
              <BellRing className="h-7 w-7 text-white" />
            </div>
            <h3 className="mt-4 font-display text-2xl leading-tight text-[#1A1A1A]">Non perderti niente</h3>
            <p className="mt-1 text-[13px] text-[#7A7A7A]">
              Attiva le notifiche: ORIO ti avvisa sul telefono anche quando l&apos;app è chiusa.
            </p>
            <ul className="mt-4 space-y-2.5">
              {PERKS.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3 text-[13.5px] text-[#1A1A1A]">
                  <span className="h-8 w-8 rounded-full bg-[#FFF3E6] flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-[#C25612]" />
                  </span>
                  {label}
                </li>
              ))}
            </ul>
            <button onClick={enable} disabled={busy} data-testid="push-optin-enable"
              className="mt-6 w-full py-3.5 rounded-full text-white font-semibold text-[15px] disabled:opacity-60 transition-transform active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg,#FF7A1A 0%,#F4B83C 100%)" }}>
              {busy ? "Attivazione…" : "Attiva notifiche"}
            </button>
            <button onClick={later} data-testid="push-optin-later"
              className="mt-2 w-full py-2.5 text-[13px] text-[#9A9A9A]">
              Più tardi
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
